import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { navLinks, site } from "../data/siteData";

const Logo = ({ light }) => (
  <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
    <span
      className={`font-display text-2xl sm:text-3xl font-semibold tracking-tight transition-colors duration-500 ${
        light ? "text-ivory" : "text-forest"
      }`}
    >
      Mango
    </span>
    <span
      className={`text-[9px] tracking-[0.3em] uppercase mt-1 transition-colors duration-500 ${
        light ? "text-ivory/70" : "text-charcoal/60"
      }`}
    >
      World Cuisine · {site.city}
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const light = !scrolled && !open;

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "bg-ivory/90 backdrop-blur-xl border-b border-line shadow-[0_1px_24px_rgba(26,54,38,0.06)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Logo light={light} />
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-sm font-medium tracking-wide transition-colors duration-300 group ${
                    light ? "text-ivory/90 hover:text-ivory" : "text-charcoal/80 hover:text-forest"
                  } ${isActive ? (light ? "text-gold" : "text-forest") : ""}`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-[width] duration-500 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/menu"
              data-testid="nav-cta-view-menu"
              className={`hidden sm:inline-flex items-center rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                light
                  ? "bg-gold text-forest hover:bg-ivory"
                  : "bg-forest text-ivory hover:bg-forest-deep"
              }`}
            >
              View Menu
            </Link>
            <button
              data-testid="nav-hamburger-button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              className={`lg:hidden p-2 rounded-full transition-colors duration-300 ${
                light ? "text-ivory" : "text-forest"
              }`}
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-forest-deep flex flex-col justify-center px-10 lg:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {navLinks.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block font-display text-4xl py-2 transition-colors duration-300 ${
                        isActive ? "text-gold" : "text-ivory hover:text-gold"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-10 text-ivory/50 text-sm"
            >
              {site.addressLines.join(" · ")}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
